/**
 * A régua do histórico de lance — só dados, sem componente e sem Supabase.
 *
 * Separado de `InsightLance.tsx` pelo motivo de sempre neste código: quem
 * importa o componente importa o cliente do Supabase junto, e um teste de
 * ordenação passaria a exigir variáveis de ambiente pra existir.
 */

/** M = Móveis/Automóveis, I = Imóveis — o mesmo código de servopa_groups_enriched. */
export type Segmento = 'M' | 'I';

export type LanceResumo = {
  group_number: number;
  lance_medio_pct: number | null;
  lance_min_pct: number | null;
  contemplados_por_assembleia: number | null;
  concorrencia_media_pct: number | null;
  assembleias_observadas: number | null;
  /**
   * Da migration 252 (ausentes enquanto ela não está no banco): típico do
   * livre SEM embutido, contemplados por assembleia nas 3 últimas e cotas do
   * grupo. `lance_medio_pct` é o típico do livre COM embutido.
   */
  lance_sem_embutido_pct?: number | null;
  contemplados_mes_recente?: number | null;
  cotas_grupo?: number | null;
  /** Ausente enquanto o cadastro do grupo não carregou, ou se ele sumiu de lá. */
  segment?: Segmento | null;
};

/**
 * Assembleias mínimas pra tratar a média como afirmação, não como ruído.
 *
 * Abaixo disso o grupo não é "barato" nem "caro" — é DESCONHECIDO, e as duas
 * coisas não podem cair no mesmo balde.
 */
export const MIN_ASSEMBLEIAS = 4;

/**
 * Só os grupos cujo histórico sustenta uma afirmação.
 *
 * O filtro não é preciosismo estatístico: `menor_lance` vem 0 quando ninguém
 * foi contemplado na assembleia (ver o cabeçalho da migration 99). Um grupo sem
 * histórico entraria no ranking como "0% de lance" — o primeiro da lista, e
 * mentira que o cliente descobre na primeira assembleia.
 */
export const comHistorico = (resumos: Iterable<LanceResumo>): LanceResumo[] =>
  [...resumos].filter(
    (l) => (l.assembleias_observadas ?? 0) >= MIN_ASSEMBLEIAS && l.lance_medio_pct != null,
  );

/**
 * Os melhores de CADA segmento, não os melhores no geral.
 *
 * O ranking cru vinha só de automóveis: são muito mais grupos, então eles
 * ocupam as três vagas por força estatística. Quem vende imóvel abria a tela e
 * o insight não falava com ele — pior que não ter insight, porque parece que
 * imóvel não tem grupo bom.
 *
 * Grupo sem segmento conhecido fica de fora: o cadastro dele não carregou ou
 * ele sumiu de `servopa_groups_enriched`, e recomendar sem saber o que é seria
 * pedir pra alguém oferecer imóvel a um cliente de carro.
 */
export const melhoresPorSegmento = (
  resumos: Iterable<LanceResumo>,
  porSegmento: number,
): { segmento: Segmento; grupos: LanceResumo[] }[] => {
  const ordenados = comHistorico(resumos).sort(
    (a, b) => (a.lance_medio_pct ?? 0) - (b.lance_medio_pct ?? 0),
  );
  return (['M', 'I'] as Segmento[])
    .map((segmento) => ({
      segmento,
      grupos: ordenados.filter((l) => l.segment === segmento).slice(0, porSegmento),
    }))
    .filter((s) => s.grupos.length > 0);
};
